/**
 * DesktopShell — persistent sidebar + content canvas for the
 * authenticated web tier (issue #433).
 *
 * At ≥1024px viewports the shell renders a 280px sticky sidebar
 * (collapsible to 72px icon-rail on demand) with the brand
 * monogram, primary nav (Caseload / Settings / District admin),
 * signed-in user, an Upgrade CTA when free-tier, and Sign out.
 * Below 1024px the sidebar hides and the children render inside
 * the existing mobile `.screen` shell — no extra chrome.
 *
 * The sidebar is sticky (not fixed) so it scrolls inside the
 * viewport without overlapping the page-level scroll. The content
 * canvas takes `flex: 1` with a max-width that grows at 1600px so
 * a 1920x1080 desktop doesn't leave dead navy on either side.
 *
 * Nav items and the Upgrade CTA navigate via the caller-supplied
 * `navigateTo` (issue #472) so sidebar clicks use the same
 * history-API pushState path as the rest of the app — no full
 * document reload, no loss of in-memory state (e.g. the caseload
 * toolbar's search/filter/sort draft from #433).
 *
 * Acceptance criteria: the shell wraps every authenticated
 * screen except /auth, /handoff/demo, and parent/public routes —
 * the AppShell in App.tsx owns that routing decision.
 */
import { useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useAuth } from '../lib/auth/useAuth';
import { useBreakpoint } from '../lib/ui/useBreakpoint';
import { useProAccess } from '../lib/paywall/access';
import { BrandMonogram } from '../lib/effectiveslp/BrandMonogram';
import { BuildingIcon } from './icons/BuildingIcon';
import { GridIcon } from './icons/GridIcon';
import { SettingsIcon } from './icons/SettingsIcon';
import { StarIcon } from './icons/StarIcon';

export interface DesktopShellProps {
  /** Pathname used to highlight the active nav item. */
  pathname: string;
  /** SPA navigator (history.pushState). Required so sidebar clicks
   *  don't trigger full document reloads (issue #472). */
  navigateTo: (path: string) => void;
  /** When true, hide the District admin nav item (caller decides). */
  hideAdminNav?: boolean;
  /** Optional breadcrumb / save indicator rendered above children. */
  contextBar?: ReactNode;
  /** The authenticated screen content. */
  children: ReactNode;
}

interface NavItem {
  /** URL pathname that activates this item. */
  match: string;
  /** Display label (always shown when sidebar is expanded). */
  label: string;
  /** Compact label shown when collapsed (≤4 chars to fit the rail). */
  short: string;
  /** Icon shown in both expanded and collapsed states. */
  icon: ReactElement;
}

const NAV_ITEMS: NavItem[] = [
  { match: '/caseload', label: 'Caseload', short: 'Case', icon: <GridIcon size={20} aria-hidden="true" /> },
  { match: '/settings', label: 'Settings', short: 'Set', icon: <SettingsIcon size={20} aria-hidden="true" /> },
  { match: '/admin/district', label: 'District admin', short: 'Admin', icon: <BuildingIcon size={20} aria-hidden="true" /> },
  { match: '/upgrade', label: 'Upgrade to Pro', short: 'Pro', icon: <StarIcon size={20} aria-hidden="true" /> },
];

/**
 * Pick the nav items relevant for this route.
 *
 * Caseload is always shown — it is the persistent sidebar's home
 * (issue #467), so even on /settings, /upgrade, and /admin routes
 * the user has a primary route back to the clinical home. Without
 * this, the sidebar would only show the route's own nav item and
 * leave the user stranded inside the same screen.
 *
 * Settings is shown on the clinician surface (/caseload, /settings,
 * /upgrade) so the gear is always one click away. It is hidden
 * inside /admin/* — district admins have their own settings route
 * at /admin/district, and a Settings link to the clinician account
 * page would be the wrong control there.
 *
 * Upgrade is hidden on regular screens because the screen itself
 * surfaces an in-context upgrade CTA when the user is
 * free-tier-exhausted. On /upgrade the sidebar still includes it
 * so the active state is visible.
 *
 * District admin item is shown whenever the caller hasn't opted
 * out via `hideAdmin`.
 */
function visibleNavItems(pathname: string, hideAdmin: boolean): NavItem[] {
  const onAdmin = pathname.startsWith('/admin/district');

  const items: NavItem[] = [];
  // Caseload is always the persistent home of the sidebar.
  items.push(NAV_ITEMS.find((n) => n.match === '/caseload')!);
  // Settings — only on the clinician surface, not inside /admin/*.
  if (!onAdmin) {
    items.push(NAV_ITEMS.find((n) => n.match === '/settings')!);
  }
  // District admin — caller opts out via hideAdmin.
  if (!hideAdmin) {
    const admin = NAV_ITEMS.find((n) => n.match.startsWith('/admin'));
    if (admin) items.push(admin);
  }
  // Upgrade is only shown on /upgrade itself so the active state
  // matches the CTA on screen; elsewhere it's a redundant card.
  if (pathname === '/upgrade') {
    items.push(NAV_ITEMS.find((n) => n.match === '/upgrade')!);
  }
  return items;
}

function isActive(pathname: string, match: string): boolean {
  if (match === '/caseload') return pathname === '/caseload' || pathname === '/';
  if (match.startsWith('/admin')) return pathname.startsWith(match);
  return pathname === match || pathname.startsWith(`${match}/`);
}

export function DesktopShell({
  pathname,
  navigateTo,
  hideAdminNav = false,
  contextBar,
  children,
}: DesktopShellProps): ReactElement {
  const bp = useBreakpoint();
  const { user, signOut } = useAuth();
  const { state: accessState } = useProAccess();
  const [collapsed, setCollapsed] = useState(false);

  // Reset the collapsed state when the viewport falls back to
  // mobile so a future desktop visit starts expanded again.
  useEffect(() => {
    if (bp === 'mobile' || bp === 'tablet') setCollapsed(false);
  }, [bp]);

  // Below 1024px: no shell chrome. The screen keeps its existing
  // mobile single-column layout untouched.
  if (bp === 'mobile' || bp === 'tablet') {
    return <>{children}</>;
  }

  const navItems = visibleNavItems(pathname, hideAdminNav);
  const isPro = accessState.kind === 'pro';
  const showUpgrade = !isPro && accessState.kind !== 'unknown';

  const railWidth = collapsed ? 72 : 280;

  return (
    <div className="desktop-shell" data-collapsed={collapsed ? 'true' : 'false'}>
      <aside
        className="desktop-shell-sidebar"
        aria-label="Primary navigation"
        style={{ width: railWidth }}
      >
        <div className="desktop-shell-brand">
          <BrandMonogram size={collapsed ? 36 : 44} />
          {!collapsed && (
            <span className="desktop-shell-brand-word">EffectiveSLP</span>
          )}
          <button
            type="button"
            className="desktop-shell-collapse"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        <nav className="desktop-shell-nav" aria-label="Sections">
          {navItems.map((item) => {
            const active = isActive(pathname, item.match);
            return (
              <a
                key={item.match}
                href={item.match}
                className={
                  active
                    ? 'desktop-shell-nav-item active'
                    : 'desktop-shell-nav-item'
                }
                aria-current={active ? 'page' : undefined}
                title={collapsed ? item.label : undefined}
                onClick={(event) => {
                  // SPA-nav instead of full document reload
                  // (issue #472) — keeps in-memory state (e.g.
                  // caseload toolbar drafts) and avoids a JS reboot.
                  // Modifier-keys + non-left clicks fall through to
                  // the browser so cmd/ctrl/middle-click "open in
                  // new tab" still works.
                  if (
                    event.defaultPrevented
                    || event.button !== 0
                    || event.metaKey
                    || event.ctrlKey
                    || event.shiftKey
                    || event.altKey
                  ) {
                    return;
                  }
                  event.preventDefault();
                  navigateTo(item.match);
                }}
              >
                <span className="desktop-shell-nav-glyph" aria-hidden="true">
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className="desktop-shell-nav-label">{item.label}</span>
                )}
                {!collapsed && (
                  <span className="desktop-shell-nav-stroke" aria-hidden="true" />
                )}
              </a>
            );
          })}
        </nav>

        <div className="desktop-shell-divider" aria-hidden="true" />

        <div className="desktop-shell-user">
          <div
            className="desktop-shell-avatar"
            aria-hidden="true"
            data-email={user?.email ?? ''}
          >
            {(user?.email ?? '?').trim().charAt(0).toUpperCase() || '?'}
          </div>
          {!collapsed && (
            <div className="desktop-shell-user-meta">
              <span className="desktop-shell-user-email" title={user?.email ?? ''}>
                {user?.email ?? 'Signed in'}
              </span>
              {showUpgrade && (
                <a
                  className="desktop-shell-upgrade"
                  href="/upgrade"
                  onClick={(event) => {
                    // SPA-nav (issue #472). Falls through to the
                    // browser on modifier-key clicks so "open in
                    // new tab" still works.
                    if (
                      event.defaultPrevented
                      || event.button !== 0
                      || event.metaKey
                      || event.ctrlKey
                      || event.shiftKey
                      || event.altKey
                    ) {
                      return;
                    }
                    event.preventDefault();
                    navigateTo('/upgrade');
                  }}
                >
                  Upgrade to Pro
                </a>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className="desktop-shell-signout"
          onClick={() => {
            void signOut();
          }}
        >
          {collapsed ? '⎋' : 'Sign out'}
        </button>
      </aside>

      <div className="desktop-shell-canvas">
        {contextBar}
        {children}
      </div>
    </div>
  );
}
